"use client";

import { disableDraftMode } from "@site/actions";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useSyncExternalStore, useTransition } from "react";

const emptySubscribe = () => () => {};
export const PREVIEW_BANNER_HEIGHT = "3rem";

export function usePreviewBannerVisibility(isDraftMode: boolean) {
  const shouldShow = useSyncExternalStore(
    emptySubscribe,
    () => window.top === window,
    () => false,
  );

  const isLocalDev = process.env.NODE_ENV === "development";

  return shouldShow && (isDraftMode || isLocalDev);
}

export default function PreviewModeBanner({ isDraftMode }: { isDraftMode: boolean }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [pending, startTransition] = useTransition();
  const showBanner = usePreviewBannerVisibility(isDraftMode);

  if (!showBanner) return null;

  const search = searchParams?.toString();
  const redirectPath = pathname || "/";
  const redirectTo = search ? `${redirectPath}?${search}` : redirectPath;
  const enableDraftHref = `/api/draft-mode/enable?redirect=${encodeURIComponent(redirectTo)}`;

  const bannerClasses = isDraftMode
    ? "border-emerald-400 bg-emerald-600 text-white"
    : "border-rose-400 bg-rose-600 text-white";

  return (
    <>
      <div
        className={`${pending ? "animate-pulse" : ""} fixed top-0 left-0 z-[80] w-full border-b ${bannerClasses} backdrop-blur`}
      >
        <div className="flex min-h-12 flex-wrap items-center justify-center gap-x-4 gap-y-2 px-4 py-2 text-sm font-medium">
          <span>
            {pending
              ? "Updating draft mode..."
              : isDraftMode
                ? "Draft mode is active."
                : "Draft mode is inactive."}
          </span>
          {!pending && (
            <div className="flex items-center gap-2">
              <a
                href={enableDraftHref}
                aria-disabled={isDraftMode}
                tabIndex={isDraftMode ? -1 : 0}
                className={`rounded-full border border-white/40 px-3 py-1 transition hover:bg-white/10 ${
                  isDraftMode ? "pointer-events-none opacity-60" : ""
                }`}
              >
                Enable drafts
              </a>
              <button
                type="button"
                disabled={pending || !isDraftMode}
                onClick={() =>
                  startTransition(() =>
                    disableDraftMode().then(() => {
                      router.refresh();
                    }),
                  )
                }
                className={`rounded-full border border-white/40 px-3 py-1 transition hover:bg-white/10 ${
                  !isDraftMode ? "cursor-not-allowed opacity-60" : ""
                }`}
              >
                Disable drafts
              </button>
            </div>
          )}
        </div>
      </div>
      <div aria-hidden className="h-12 w-full" style={{ height: PREVIEW_BANNER_HEIGHT }} />
    </>
  );
}
